import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { StatCount } from 'components/common/stats';
import { UsersSendMessage } from 'components/users';
import {
  AchievementCompletionsAccessRequests,
  AchievementGroupOfUsersAdd,
  AchievementUsersSubHeader,
  AchievementUsersTable
} from '.';

import type { GetSuperviseesAchievementsRes } from 'shared-types/responses';
import type { AchievementUsersView } from './AchievementUsersSubHeader';

interface Props {
  achievementId: number;
  isMyTeamPage?: boolean;
  supervisorId: number;
}

const AchievementSupervisedUsers = ({
  achievementId,
  isMyTeamPage,
  supervisorId
}: Props) => {
  const {
    messages,
    pageState: { achievement },
    serverConfig: { adminUrl }
  } = useReduxState();

  const router = useRouter();
  const queryKeys = Object.keys(router.query);
  const focusedUserId = router.query.focusedUserId;

  const { isGroupAchievement } = useAchievementTypes({
    type: achievement.type
  });

  const { data, loading, updateData } =
    useApiData<GetSuperviseesAchievementsRes>({
      url:
        `/admin/supervisors/${supervisorId}/supervisees-achievements/${achievementId}` +
        (queryKeys.length > 0
          ? `?${queryKeys
              .filter((key) => key !== 'focusedUserId')
              .map((key) => `${key}=${router.query[key]}`)
              .join('&')}`
          : ''),
      onlyIf: Boolean(achievementId && supervisorId)
    });

  const handleBackClick = () => {
    const queryParams = { ...router.query };
    delete queryParams.focusedUserId;

    router.replace({ query: queryParams }, undefined, { shallow: true });
  };

  const title = isMyTeamPage
    ? messages.myTeamPage
    : messages.adminSupervisorsSupervisees;

  const listOfGroupCompletionIds = data?.listOfGroupCompletionIds || [];
  const listOfUserIds = data?.listOfUserIds || [];

  return (
    <div>
      <AchievementCompletionsAccessRequests
        supervisorId={supervisorId}
        updateUsers={updateData}
      />
      <Box
        headerContent={
          <FlexContainer style={{ gap: 10 }}>
            <UsersSendMessage
              buttonSize="header"
              emailButtonTarget={`${adminUrl}/achievements/${achievement.id}`}
              id="achievement-supervised-users-send-message"
              supervisorRouteSupervisorId={supervisorId}
              userSelectionParams={{
                excludeUsersWithoutEmail: true,
                routerQueryParams: {
                  achievementId: `${achievement.id}`,
                  ...router.query
                },
                userSelector: 'emails'
              }}
            />

            <MoreOptions category="light">
              <ApiDownloadButton
                category="success"
                filename={`${achievement.title}.xlsx`}
                icon="file-excel"
                packet={{
                  achievementIds: [achievement.id],
                  isSupervisorContext: true,
                  userIds: listOfUserIds,
                  ...(isGroupAchievement && listOfGroupCompletionIds?.length
                    ? { groupCompletionIds: listOfGroupCompletionIds }
                    : {})
                }}
                text={messages.commonExportToExcel}
                url="/admin/achievements/excel-export"
              />

              {isGroupAchievement ? (
                <AchievementGroupOfUsersAdd
                  isSupervisorPage={true}
                  supervisedUserIds={data?.supervisedUserIds || []}
                  updateGroups={updateData}
                />
              ) : null}
            </MoreOptions>
          </FlexContainer>
        }
        loading={loading}
        padding={0}
        subHeaderContent={<AchievementUsersSubHeader isSupervisorPage={true} />}
        title={
          focusedUserId ? (
            <BoxBackLink
              onBackClick={handleBackClick}
              simpleReturn={true}
              text={messages.usersAllUsers}
            />
          ) : (
            <>
              {title}

              <StatCount
                isLarge={true}
                style={{ marginLeft: 5 }}
                value={data?.numberOfUsers || 0}
              />
            </>
          )
        }
      >
        {!data?.supervisees?.length ? (
          <EmptyIndicator icon="sitemap" title={messages.commonNoUsers} />
        ) : (
          <AchievementUsersTable
            isSupervisorPage={true}
            supervisedUserIds={data?.supervisedUserIds || []}
            users={
              focusedUserId
                ? data.supervisees
                    .filter((user) => user.id === Number(focusedUserId))
                    .map(({ completions, ...user }) =>
                      completions.map((completion) => ({
                        ...user,
                        completions: [completion]
                      }))
                    )
                    .flat()
                : data.supervisees
            }
            updateUsers={updateData}
            view={(router.query.view as AchievementUsersView) || 'default'}
          />
        )}
      </Box>

      {focusedUserId ? null : (
        <Pagination
          currentPage={router.query.page ? Number(router.query.page) : 1}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => {
            router.replace({ query: { ...router.query, page } }, undefined, {
              shallow: true
            });
          }}
        />
      )}
    </div>
  );
};

export default AchievementSupervisedUsers;
